/**
 * Settings: what is true for you and for the whole workspace, a list of sections on the left and the
 * chosen one beside it. Each agent's own settings live beside the agent; a connection's own details
 * live in Apps. Everything is saved as it is changed.
 */
import { useEffect, useState } from 'react'
import { DEFAULT_COMPACT_AT, type CompactSetting } from '@shared/compaction'
import { DEFAULT_AUTOMATION, type AutomationSetting } from '@shared/types'
import { Pick } from './Agents'
import { allowanceApi, api, integrations, xaiApi, type Allowance, type Skill, type XaiSubscriptionStatus } from './api'
import { TelegramPart } from './AppParts'
import { CopyHub } from './CopyHub'
import { TINTS } from './data'
import { Btn, Dialog, Field, KeyCap, Status, Switch, Tabs, askName, askSure, cx, inputCls } from './kit'
import { Logo } from './logos'
import { SharedMemory } from './Memory'
import { Bar, useTabs } from './Page'
import { People } from './People'
import { Group, Num, Row, Rows, note, quiet, value } from './rows'
import { TradingSettings } from './SettingsTrading'
import { useDemo } from './store'

export { Group, Num, Row, Rows, quiet, value } from './rows'

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'workspace', label: 'Workspace' },
  { id: 'account', label: 'Account' },
  { id: 'usage', label: 'Usage' },
  { id: 'memory', label: 'Memory' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'trading', label: 'Trading' },
  { id: 'copy', label: 'Copy trading' },
  { id: 'shortcuts', label: 'Shortcuts' },
  { id: 'about', label: 'About' },
]

/** On a phone the sections are a row of tabs; on a wide screen they are the list beside the page. */
export function SettingsBar() {
  const t = useTabs()
  return (
    <div className="md:hidden">
      <Bar>
        <div className="-mx-2 flex min-w-0 overflow-x-auto px-2 [scrollbar-width:none]">
          <Tabs options={TABS} value={t.settings} onChange={(v) => t.set({ settings: v })} />
        </div>
      </Bar>
    </div>
  )
}

const Keys = ({ keys }: { keys: string[] }) => (
  <span className="flex gap-[2px]">
    {keys.map((k) => (
      <KeyCap key={k}>{k}</KeyCap>
    ))}
  </span>
)
const when = (ms: number) => new Date(ms).toLocaleString('en-US', { weekday: 'long', hour: 'numeric', minute: '2-digit' })
const at = (ms: number) => new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const fail = (e: unknown) => useDemo.getState().say(e instanceof Error ? e.message : 'That did not work')

/** What you are called here — on the screen and by every agent. Saved when you leave the field or press Enter. */
function NameField() {
  const user = useDemo((s) => s.user)
  const rename = useDemo((s) => s.rename)
  const now = user?.name || user?.username || ''
  const [v, setV] = useState(now)
  useEffect(() => setV(now), [now])
  const commit = () => {
    const name = v.replace(/\s+/g, ' ').trim()
    if (!name) return setV(now)
    if (name !== now) void rename(name)
  }
  return <input value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} maxLength={40} className={inputCls} aria-label="What agents call you" />
}

/** Where you are, read from this device. */
function zone() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const off = new Intl.DateTimeFormat('en-US', { timeZoneName: 'shortOffset' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? ''
  return `${(tz.split('/').pop() ?? tz).replace(/_/g, ' ')}, ${off}`
}

/* ── agents working with nobody at the screen ────────────────────────── */

type Wakes = Awaited<ReturnType<typeof api.wakes>>
const SOURCE: Record<string, string> = { task: 'Routine', watch: 'Watcher', scan: 'Scan', worker: 'Worker', ai: 'Follow-up' }
function Automation() {
  const settings = useDemo((s) => s.settings)
  const save = useDemo((s) => s.saveSettings)
  const openChat = useDemo((s) => s.openChat)
  const [w, setW] = useState<Wakes | null>(null)
  useEffect(() => {
    void api.wakes().then(setW, () => {})
  }, [])
  const a: AutomationSetting = { ...DEFAULT_AUTOMATION, ...(settings?.automation ?? {}) }
  const put = (p: Partial<AutomationSetting>) => void save({ automation: { ...a, ...p } })
  const recent = (w?.wakes ?? []).slice(0, 8)
  return (
    <>
      <Group title="Agents working by themselves" note={w ? (w.budget.unlimited ? `${w.budget.used} today · no limit on your Grok plan` : `${w.budget.used} of ${w.budget.max} used today`) : undefined}>
        <Rows>
          <Row title="Runs a day nobody asked for" sub="Routines, watchers, scans and worker callbacks share it. Past it they are skipped, and you are told once" right={<Num value={a.wakeBudget} min={0} max={1000} label="Runs a day nobody asked for" onSave={(n) => put({ wakeBudget: Math.round(n) })} />} />
          <Row title="An agent may plan its own follow-up" sub="When it finds something it can come back to the same conversation later. Each one is listed there and can be cancelled" right={<Switch on={a.aiSchedule} onChange={(aiSchedule) => put({ aiSchedule })} label="An agent may plan its own follow-up" />} />
          {a.aiSchedule && <Row title="Follow-ups in one conversation" sub="At most this many waiting at once" right={<Num value={a.aiPerChat} min={0} max={50} label="Follow-ups in one conversation" onSave={(n) => put({ aiPerChat: Math.round(n) })} />} />}
        </Rows>
      </Group>
      {recent.length > 0 && (
        <Group title="The last runs">
          <Rows>
            {recent.map((x) => (
              <Row key={x.id} title={x.title || SOURCE[x.source] || x.source} sub={`${SOURCE[x.source] ?? x.source} · ${x.status}${x.detail ? ` · ${x.detail}` : ''}`} right={<span className="shrink-0 text-[12px] tracking-normal text-ws-fg-3">{at(x.at)}</span>} onClick={x.chatId ? () => void openChat(x.chatId!) : undefined} />
            ))}
          </Rows>
        </Group>
      )}
    </>
  )
}

/* ── skills: instructions every agent follows while one is on ─────────── */

function SkillDialog({ skill, onClose, onChanged }: { skill: Skill; onClose: () => void; onChanged: () => void }) {
  const [name, setName] = useState(skill.name)
  const [text, setText] = useState(skill.instructions)
  const save = () => void integrations.skillUpdate(skill.id, { name: name.trim() || skill.name, instructions: text }).then(() => (onChanged(), onClose()), fail)
  return (
    <Dialog
      open
      onClose={onClose}
      title="Skill"
      width={640}
      footer={
        <>
          <Btn variant="danger" className="mr-auto px-3" onClick={() => askSure({ title: `Delete “${skill.name}”?`, body: 'Agents stop following it. The text is deleted.', cta: 'Delete', done: () => void integrations.skillDelete(skill.id).then(() => (onChanged(), onClose()), fail) })}>
            Delete
          </Btn>
          <Btn variant="outline" onPanel className="px-3" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="white" className="px-3" onClick={save}>
            Save
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </Field>
        <Field label="What agents should do" hint="Read at the start of every answer while the skill is on">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} placeholder="A house style for analysis, a checklist, a way of working…" className="w-full resize-y rounded-[8px] border border-ws-line bg-ws-field px-[10px] py-2 text-[13px] leading-5 tracking-normal transition-colors duration-150 focus:border-ws-focus" />
        </Field>
      </div>
    </Dialog>
  )
}
function Skills() {
  const [skills, setSkills] = useState<Skill[] | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const load = () => void integrations.skills().then(setSkills, () => setSkills([]))
  useEffect(load, [])
  const editing = skills?.find((s) => s.id === open)
  return (
    <Group title="Skills" note="Instructions agents follow while one is on">
      <Rows>
        {(skills ?? []).map((s) => (
          <Row
            key={s.id}
            title={s.name}
            sub={s.instructions.trim().split('\n')[0]?.slice(0, 90) || 'Nothing written yet'}
            right={
              <span className="flex shrink-0 items-center gap-2">
                <button type="button" className={quiet} onClick={() => setOpen(s.id)}>
                  Edit
                </button>
                <Switch on={s.enabled} onChange={(enabled) => (setSkills((xs) => (xs ?? []).map((x) => (x.id === s.id ? { ...x, enabled } : x))), void integrations.skillUpdate(s.id, { enabled }).catch(fail))} label={s.name} />
              </span>
            }
          />
        ))}
        <Row
          title="A new skill"
          sub="A way of working written once, for every agent"
          right={
            <button type="button" className={quiet} onClick={() => askName({ title: 'New skill', cta: 'Create', done: (name) => void integrations.skillCreate(name, '').then((s) => (load(), setOpen(s.id)), fail) })}>
              Add
            </button>
          }
        />
      </Rows>
      {editing && <SkillDialog skill={editing} onClose={() => setOpen(null)} onChanged={load} />}
    </Group>
  )
}

/* ── usage ───────────────────────────────────────────────────────────── */

/** What is left of the week's allowance — one figure for all agents together. */
function AllowanceCard() {
  const [a, setA] = useState<Allowance | null>(null)
  useEffect(() => {
    let on = true
    allowanceApi().then((x) => on && setA(x), () => on && setA({ known: false, note: 'The plan did not answer' }))
    return () => {
      on = false
    }
  }, [])
  if (!a) return <div className="h-[104px] rounded-[8px] border border-ws-line" />
  if (!a.known)
    return (
      <div className="rounded-[8px] border border-ws-line px-4 py-[14px]">
        <div className="text-[13px] tracking-normal text-ws-fg-2">{a.note ?? 'The plan does not say how much is left'}</div>
        <p className="m-0 mt-2 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          The figure comes from your Grok plan itself. It is also shown at{' '}
          <a href="https://grok.com/?_s=usage" target="_blank" rel="noreferrer" className="text-ws-fg-2 underline decoration-ws-rule underline-offset-2 hover:text-ws-fg">
            grok.com
          </a>
          .
        </p>
      </div>
    )
  const left = Math.max(0, Math.min(100, Math.floor(a.percentLeft ?? 0)))
  return (
    <div className="rounded-[8px] border border-ws-line px-4 pb-4 pt-[14px]">
      <div className="flex items-baseline gap-2">
        <span className="text-[28px] font-medium leading-8 tracking-[-0.01em] text-ws-fg">{left}%</span>
        <span className="text-[13px] tracking-normal text-ws-fg-2">left {a.period === 'monthly' ? 'this month' : 'this week'}</span>
        {a.resetsAt && <span className="ml-auto text-[12px] tracking-normal text-ws-fg-3">Resets {when(a.resetsAt)}</span>}
      </div>
      <div className="mt-3 h-[6px] overflow-hidden rounded-[3px] bg-ws-edge" role="img" aria-label={`${left}% of the allowance is left`}>
        <div className={cx('h-full rounded-[3px]', left <= 10 ? 'bg-ws-down' : 'bg-ws-fg')} style={{ width: `${left}%` }} />
      </div>
      <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">One allowance for every agent: your {a.plan ?? 'Grok'} plan. Background jobs and routines draw from it too.</p>
    </div>
  )
}

const KEEP: { label: string; value: CompactSetting; about: string }[] = [
  { label: '200k', value: 200_000, about: 'Tidies up early: each answer costs less, a long thread loses detail sooner.' },
  { label: '300k', value: 300_000, about: 'The usual: long threads stay whole, and answers stay quick.' },
  { label: '400k', value: 400_000, about: 'Keeps more in mind; each answer in a long thread uses the allowance faster.' },
  { label: 'All of it', value: 'window', about: 'Tidies up only when the model can hold no more. The dearest answers in a long thread.' },
]

/* ── notifications ───────────────────────────────────────────────────── */

function TelegramDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog
      open
      onClose={onClose}
      title="Telegram"
      footer={
        <Btn variant="white" className="px-3" onClick={onClose}>
          Done
        </Btn>
      }
    >
      <div className="-mt-4">
        <TelegramPart />
      </div>
    </Dialog>
  )
}

/* ── the page ────────────────────────────────────────────────────────── */

export function SettingsView() {
  const t = useTabs()
  const tab = t.settings
  const user = useDemo((s) => s.user)
  const admin = user?.role === 'admin'
  const settings = useDemo((s) => s.settings)
  const save = useDemo((s) => s.saveSettings)
  const agents = useDemo((s) => s.agents.length)
  const spaces = useDemo((s) => s.spaces)
  const spaceId = useDemo((s) => s.spaceId)
  const patchSpace = useDemo((s) => s.patchSpace)
  const removeSpace = useDemo((s) => s.removeSpace)
  const logout = useDemo((s) => s.logout)
  const go = useDemo((s) => s.go)
  const space = spaces.find((x) => x.id === spaceId)
  const [xai, setXai] = useState<XaiSubscriptionStatus | null>(null)
  const [telegram, setTelegram] = useState(false)
  useEffect(() => {
    if (tab === 'usage') void xaiApi.status().then(setXai, () => {})
  }, [tab])
  const keep = KEEP.find((k) => k.value === (settings?.compactAt ?? DEFAULT_COMPACT_AT)) ?? KEEP[1]
  return (
    <div className="mx-auto flex w-full max-w-[920px] gap-10 px-4 pb-10 pt-5 md:px-10 md:pt-8">
      <nav className="sticky top-8 hidden w-[168px] shrink-0 flex-col gap-px self-start md:flex" aria-label="Settings">
        {TABS.map((x) => (
          <button key={x.id} type="button" onClick={() => t.set({ settings: x.id })} aria-current={tab === x.id} className={cx('flex h-8 items-center rounded-[8px] px-[10px] text-left transition-colors duration-150', tab === x.id ? 'bg-ws-panel text-ws-fg' : 'text-ws-fg-3 hover:bg-ws-hover hover:text-ws-fg')}>
            {x.label}
          </button>
        ))}
      </nav>

      <div className="min-w-0 max-w-[640px] flex-1 space-y-6">
        {tab === 'general' && (
          <>
            <Group title="What agents call you">
              <NameField />
            </Group>
            <Group title="New agents answer in">
              <Pick options={['Türkçe', 'English']} value={(settings?.agentLang ?? '') as 'Türkçe' | 'English'} onChange={(agentLang) => void save({ agentLang })} />
              {!settings?.agentLang && <p className={note}>Not chosen: a new agent answers in the language you write to it in. Each agent’s own language is in its settings.</p>}
            </Group>
            <Group title="Time">
              <Rows>
                <Row title="Your time zone" right={<span className={value}>{zone()}</span>} />
                <Row title="Market time" sub="Schedules of routines are written in it" right={<span className={value}>New York, ET</span>} />
              </Rows>
            </Group>
            <Group title="Answers">
              <Rows>
                <Row title="Search the web while answering" sub="Agents look things up live, on the web and on X" right={<Switch on={settings?.liveSearch !== false} onChange={(v) => void save({ liveSearch: v })} label="Search the web while answering" />} />
              </Rows>
            </Group>
            <Automation />
            <Skills />
          </>
        )}

        {tab === 'workspace' && space && (
          <>
            <Group title="Name">
              <input defaultValue={space.name} key={space.id} onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== space.name && patchSpace(space.id, { name: e.target.value.trim() })} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} className={inputCls} aria-label="Workspace name" />
            </Group>
            {space.id !== spaces[0]?.id && (
              <Group title="Mark">
                <div className="flex gap-2">
                  {TINTS.map((x) => (
                    <button key={x} type="button" aria-label="Mark colour" aria-pressed={space.tint === x} onClick={() => patchSpace(space.id, { tint: x })} className={cx('flex h-8 w-8 items-center justify-center rounded-[8px] border transition-colors duration-150', space.tint === x ? 'border-ws-focus bg-ws-panel' : 'border-ws-line hover:bg-ws-hover')}>
                      <span className="block h-4 w-4 rounded-[5px]" style={{ background: x }} />
                    </button>
                  ))}
                </div>
              </Group>
            )}
            <Group title="What it has" note="Connected in Apps">
              <Rows>
                <Row
                  left={<Logo name="grok" />}
                  title="AI account"
                  sub="Required: every agent here runs on it"
                  right={
                    <button type="button" className={quiet} onClick={() => go('apps')}>
                      Open Apps
                    </button>
                  }
                />
                <Row title="Agents" right={<span className={value}>{agents}</span>} />
              </Rows>
            </Group>
            {spaces.length > 1 && space.id !== spaces[0]?.id && (
              <Btn
                variant="danger"
                className="px-3"
                onClick={() =>
                  askSure({
                    title: `Delete ${space.name}?`,
                    body: 'Its agents and group chats leave the screen. Your broker accounts and your other workspaces are not touched.',
                    cta: 'Delete workspace',
                    done: () => removeSpace(space.id),
                  })
                }
              >
                Delete this workspace
              </Btn>
            )}
          </>
        )}

        {tab === 'account' && user && (
          <>
            <Group title="You">
              <Rows>
                <Row left={<span className="block h-6 w-6 shrink-0 rounded-full bg-[linear-gradient(135deg,#ff9bd2,#b28cff)]" />} title={user.name || user.username} sub={`@${user.username}${admin ? ' · runs the server' : ''}`} right={<Status tone="free">Signed in</Status>} />
              </Rows>
            </Group>
            <Group title="Your name" note="Agents call you this too">
              <NameField />
            </Group>
            <Btn variant="outline" className="px-3" onClick={() => void logout()}>
              Sign out
            </Btn>
            {admin && <People />}
          </>
        )}

        {tab === 'usage' && (
          <>
            <Group title="What is left">
              <AllowanceCard />
            </Group>
            <Group title="What pays for the agents">
              <Rows>
                <Row
                  left={<Logo name="grok" />}
                  title={xai?.connected ? 'Your Grok plan' : admin ? 'The server’s API key' : 'No AI account yet'}
                  sub={xai?.connected ? `${agents} agents run on it${xai.email ? ` · ${xai.email}` : ''}` : admin ? 'Paid by use. Connect your Grok plan in Apps to run on it instead' : 'Connect your Grok plan in Apps'}
                  right={
                    xai?.connected ? (
                      <Status tone="free">Connected</Status>
                    ) : (
                      <button type="button" className={quiet} onClick={() => go('apps')}>
                        Open Apps
                      </button>
                    )
                  }
                />
                {xai?.connected && <Row title="Last 7 days" sub="Answers and background runs" right={<span className={value}>{xai.week.subscription.runs + xai.week.api.runs} runs</span>} />}
              </Rows>
            </Group>
            {xai?.connected && admin && (
              <Group title="When the allowance runs out">
                <Pick options={['Wait', 'Use the API key']} value={xai.fallbackToApi ? 'Use the API key' : 'Wait'} onChange={(v) => (setXai({ ...xai, fallbackToApi: v === 'Use the API key' }), void xaiApi.setFallback(v === 'Use the API key').catch(fail))} />
                <p className={note}>{xai.fallbackToApi ? 'Agents go on, paid by use on the server’s API key, until the allowance resets.' : 'Agents stop until the allowance resets. A worker’s orders at the broker stay where they are.'}</p>
              </Group>
            )}
            <Group title="How much one conversation keeps in mind">
              <Pick options={KEEP.map((k) => k.label)} value={keep.label} onChange={(v) => void save({ compactAt: KEEP.find((k) => k.label === v)!.value === DEFAULT_COMPACT_AT ? null : KEEP.find((k) => k.label === v)!.value })} />
              <p className={note}>Past that, an agent tidies the conversation up by itself. {keep.about}</p>
            </Group>
          </>
        )}

        {tab === 'memory' && <SharedMemory />}

        {tab === 'notifications' && (
          <>
            <Group title="Where agents reach you">
              <Rows>
                <Row
                  left={<Logo name="telegram" />}
                  title="Telegram"
                  sub={settings?.telegramConfigured ? (settings.telegramChatId ? 'Connected' : 'A bot is saved — your chat with it is not chosen yet') : 'Not connected'}
                  right={
                    <span className="flex shrink-0 items-center gap-2">
                      {settings?.telegramConfigured && settings.telegramChatId && <Status tone="free">On</Status>}
                      <button type="button" className={quiet} onClick={() => setTelegram(true)}>
                        {settings?.telegramConfigured ? 'Change' : 'Set up'}
                      </button>
                    </span>
                  }
                />
              </Rows>
            </Group>
            <Group title="When">
              <Rows>
                <Row title="Something needs a decision" sub="An order or a change waiting for you" right={<Switch on disabled label="Decisions" />} />
                <Row title="A worker trades" sub="Fills, orders placed, a worker closing, a problem" right={<Switch on={settings?.tradeNotify !== false} onChange={(tradeNotify) => void save({ tradeNotify })} label="A worker trades" />} />
                {settings?.tradeNotify !== false && <Row title="…and when a rule moves something" sub="A stop pulled up, a target lowered, an entry cancelled" right={<Switch on={!!settings?.tradeNotifyRules} onChange={(tradeNotifyRules) => void save({ tradeNotifyRules })} label="When a rule moves something" />} />}
              </Rows>
              <p className={note}>Each agent has its own switch too, in its settings: whether it may write to you when a routine or a background job finishes.</p>
            </Group>
            {telegram && <TelegramDialog onClose={() => setTelegram(false)} />}
          </>
        )}

        {tab === 'trading' && <TradingSettings />}

        {tab === 'copy' && <CopyHub />}

        {tab === 'shortcuts' && (
          <Group title="Keyboard and pointer">
            <Rows>
              <Row title="Search and commands" right={<Keys keys={['⌘', 'K']} />} />
              <Row title="Agents" right={<Keys keys={['⌘', 'Y']} />} />
              <Row title="Send" right={<Keys keys={['↵']} />} />
              <Row title="New line" right={<Keys keys={['⇧', '↵']} />} />
              <Row title="Close what is open" right={<Keys keys={['esc']} />} />
              <Row title="More on anything" sub="An agent, a folder, a chat, a card" right={<span className={value}>Right-click, or a long press</span>} />
            </Rows>
          </Group>
        )}

        {tab === 'about' && (
          <Group title="Liberabank">
            <Rows>
              <Row
                title="The screens from before agents"
                sub="The same data, the older look — kept as the way back"
                right={
                  <a href="/classic.html" className={quiet}>
                    Open
                  </a>
                }
              />
            </Rows>
          </Group>
        )}
      </div>
    </div>
  )
}
